import React, { useState, useCallback, useEffect } from "react";

import {
    Box,
    Paper,
    Divider,
    TextField,
    Grid,
    Typography,
    Switch,
    FormControlLabel,
    Select,
    FormControl,
    InputLabel,
    FormHelperText,
    MenuItem,
    OutlinedInput,
    Chip,
    Checkbox,
    ListItemText,
    styled,
    List,
    ListItem
} from '@mui/material';

import {
    LoadingButton
} from '@mui/lab';

import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from 'formik';
import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonRestricted from '../../common/CommonRestricted';

import { mergeSchema } from "../../../utils/dataSchema"
import { spliceString } from "../../../utils/formatString";

import { useBranchUsers, branchUserTypes, targetTypes as branchUserTargetTypes } from '../../../hooks/api/useBranchUsers';
import { useBranches, branchTypes } from "../../../hooks/api/useBranches";
import { useCustomFields, customFieldFunctions, customFieldModules } from '../../../hooks/api/useCustomFields';

import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { useAppContextFeatures } from '../../../context/AppContext';
import {
    initialValues, initialBranchValues, initialPasswordValues,
    validationSchema, validationBranchSchema, validationPasswordSchema
} from "../../../schema/users/branchUserSchema";
import { inputTypes, inputTypesDef } from "../../../schema/customFields/customFieldSchema"

import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';

import { useStateCustomFields } from "../../../hooks/state/useStateCustomFields";
import { useTranslations } from "../../../hooks/useTranslations";

const StyledCard = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2)
}));

export default function UpdateBranchUser({
    id,
    routeType,
    handleOnSuccess
}) {

    const { t } = useTranslations({ ns: ["common", "@user", "_prompt", "_common"] })

    const { id: parentId } = useParams();

    const { promptMessage } = useSystemPrompt();

    const { isLoading, get, update } = useBranchUsers({ branchUserType: branchUserTypes.customer, targetType: branchUserTargetTypes.customer });
    const { list: listCustomFields } = useCustomFields({ customFieldModule: customFieldModules.user, customFieldFunction: customFieldFunctions.user.customerBranchUser })

    const { list: listBranches, filterOperatorTypes } = useBranches({ branchType: branchTypes.customer });
    const { isPermissionAllow } = usePermissions(
        routeType,
        scopeIds.branchUsers.id,
        scopeTypes[scopeIds.branchUsers.id].customer.id
    )

    const {
        dataCustomFields,

        getInitialFormStateRows,
        getSubmitFormDataFromRows,

        getCustomFieldOptionNameByIds,

        RenderCustomFieldSelectionTextbox,
        RenderCustomFieldSelectionDropdown,
        RenderCustomFieldSelectionMultiDropdown
    } = useStateCustomFields(
        {
            fieldModule: customFieldModules.user,
            moduleFunction: customFieldFunctions.user.customerBranchUser
        }
    )

    const { setSystemLoading } = useAppContextFeatures()

    const navigate = useNavigate();
    const [savedValues, setSavedValues] = useState()
    const { getRoute } = useSystemRoutes(routeTypes.admin);
    const [dataBranches, setDataBranches] = useState([])
    const [customFields, setCustomFields] = useState([])

    // const validationSchemaCombine = mergeSchema(validationSchema, validationBranchSchema);

    const initialValuesCombine = {
        ...initialValues,
        ...initialPasswordValues,
        ...initialBranchValues
    }

    const [checked, setChecked] = useState(false);

    const getSchema = () => {
        if (checked === true) {
            return mergeSchema(validationSchema, validationBranchSchema, validationPasswordSchema);
        }
        return (validationSchema, validationBranchSchema)
    }

    const handleAllowChangePassword = (event) => {
        setChecked(event.target.checked);
    };

    const loadData = async (id) => {

        setSystemLoading(true)

        try {

            const { data } = await get({ id })
            const { branch_user: branchUser, branches, custom_fields: savedCustomFields = [] } = data;

            const cf = await getInitialFormStateRows({
                savedCustomFields,
            })

            setSavedValues({
                ...initialValuesCombine,
                username: branchUser.username,
                firstname: branchUser.firstname,
                lastname: branchUser.lastname,
                contact: branchUser.contact,
                email: branchUser.email,
                status: branchUser?.status === '1',
                remark: branchUser.remark,
                branchAccessId: branches.map(i => i.branch_id),
                rows: cf
            })

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false)

    }

    const onSubmit = async (currentValues) => {

        try {
            const { rows } = values;

            let cf = getSubmitFormDataFromRows({ rows })

            let data = {
                credential: {
                    firstname: currentValues?.firstname,
                    lastname: currentValues?.lastname,
                    password: currentValues?.password,
                    contact: currentValues?.contact,
                    email: currentValues?.email,
                    status: currentValues?.status ? 1 : 0,
                },
                branch_user: {
                    // user_id: parentId
                },
                props: {
                    branch_id: currentValues?.branchAccessId
                },
                custom_fields: cf,

            }
            const response = await update({ id, data });

            promptMessage(true, `${response?.message}`)
            // navigate(getRoute(`users/customer/${customerId}/branchUsers`))
            if (handleOnSuccess) handleOnSuccess()

        } catch (error) {
            promptMessage(false, `Action Failed. (${error?.message})`)
        }
    }

    const fetchDataBranch = async (parentId) => {

        try {

            const props = {
                limit: -1,
                skip: 0,
                filterValue: [
                    {
                        name: "user_id",
                        operator: filterOperatorTypes.eq,
                        value: parentId
                    }
                ],
                sortInfo: {
                    id: "id",
                    dir: 0,
                }
            }

            const { data } = await listBranches(props)

            // only take count branch + branch_shipping (which not shipping_addres)
            const validBranches = data?.branches.filter(i => i.type !== "shipping_address")

            setDataBranches(validBranches)

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }
    }

    useEffect(() => {

        loadData(id);
        fetchDataBranch(parentId);

    }, [])

    const formik = useFormik({
        initialValues: savedValues,
        validationSchema: getSchema(),
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue, handleSubmit } = formik;

    const RenderChangePassword = () => {

        if (checked === false) return <></>

        return (
            <>
                <Grid item xs={12} sm={12} md={6} textAlign={"left"}>
                    <TextField
                        margin="dense"
                        size="small"
                        fullWidth
                        name={"password"}
                        label={t('password')}
                        type="password"
                        variant="outlined"
                        value={values.password}
                        onChange={handleChange}
                        error={touched.password && Boolean(errors.password)}
                        helperText={touched.password && errors.password}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={6} textAlign={"left"}>
                    <TextField
                        margin="dense"
                        size="small"
                        fullWidth
                        name={"passwordconfirm"}
                        label={t('confirmPassword')}
                        type="password"
                        variant="outlined"
                        value={values.passwordconfirm}
                        onChange={handleChange}
                        error={touched.passwordconfirm && Boolean(errors.passwordconfirm)}
                        helperText={touched.passwordconfirm && errors.passwordconfirm}
                    />
                </Grid>
            </>
        )

    }

    const RenderCustomFieldSelection = () => {

        if (!values?.rows) return <></>

        const rows = values.rows

        return (
            <>
                <Grid item xs={12}>
                    <Divider sx={{ mt: 2, mb: 1 }} />
                </Grid>

                <Grid item xs={12} sm={12} md={12} textAlign={"left"}>
                    <CommonSectionHeader
                        title={t('customProperties')}
                        subTitle={t('provideCustomItemInformation')}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={12}>
                    <List>
                        {
                            values.rows.map((item, index) => {

                                const { rows } = values

                                let itemValues = rows.length ? rows[index] : {}

                                let {
                                    fieldType,
                                    fieldLabel,
                                    fieldKey
                                } = item

                                return (
                                    <ListItem
                                        key={index}
                                        divider
                                        disablePadding
                                        sx={{
                                            py: 1
                                        }}
                                    >
                                        <Grid container spacing={1}>
                                            <Grid item md={3} xs={12} sm={12} display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"} >
                                                <Chip
                                                    label={fieldLabel}
                                                    color='primary'
                                                    variant='outlined'
                                                    size='large'
                                                    sx={{
                                                        width: '100%',
                                                        // textAlign: 'left',
                                                        fontSize: 16,
                                                        fontWeight: 800
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item md={9} xs={12} sm={12}>

                                                {
                                                    fieldType === inputTypesDef.textbox &&

                                                    RenderCustomFieldSelectionTextbox({
                                                        index,
                                                        item,
                                                        itemValues,
                                                        inputProps: {
                                                            fullWidth: true,
                                                            name: `rows.${index}.customValue`,
                                                            label: itemValues?.fieldLabel,
                                                            type: "text",
                                                            variant: "outlined",
                                                            value: itemValues?.customValue,
                                                            onChange: (event) => setFieldValue(`rows.${index}.customValue`, event.target.value),
                                                            // error: Boolean(itemErrors?.customValue),
                                                            // helperText: itemErrors?.customValue
                                                        }
                                                    })
                                                }

                                                {
                                                    fieldType === inputTypesDef.dropdown &&

                                                    RenderCustomFieldSelectionDropdown({
                                                        index,
                                                        item,
                                                        itemValues,
                                                        selectProps: {
                                                            labelId: "customValue",
                                                            label: itemValues?.fieldLabel,
                                                            name: `rows.${index}.customArray`,
                                                            onChange: (event) => {
                                                                setFieldValue(`rows.${index}.customArray`, [event.target.value])
                                                            },
                                                            size: 'small',
                                                            margin: 'dense',
                                                            value: itemValues.customArray.length ? itemValues.customArray[0] : ""
                                                        }
                                                    })
                                                }

                                                {
                                                    fieldType === inputTypesDef.multidropdown &&

                                                    RenderCustomFieldSelectionMultiDropdown({
                                                        index,
                                                        item,
                                                        itemValues,
                                                        selectProps: {
                                                            labelId: itemValues?.fieldLabel,
                                                            label: itemValues?.fieldLabel,
                                                            name: `rows.${index}.customArray`,
                                                            onChange: handleChange,
                                                            renderValue: (selected) => {

                                                                return (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                                        {
                                                                            getCustomFieldOptionNameByIds(selected, dataCustomFields, fieldKey).map((i, index) => (
                                                                                <Chip
                                                                                    key={index}
                                                                                    // color={'red'}
                                                                                    label={spliceString({ val: i, maxLength: 20 })}
                                                                                />
                                                                            ))
                                                                        }
                                                                    </Box>
                                                                )

                                                            },
                                                            size: 'small',
                                                            margin: 'dense',
                                                            multiple: true,
                                                            input: <OutlinedInput id="select-multiple-chip" label="Chip" />,
                                                            value: itemValues?.customArray ? itemValues.customArray : []
                                                        }
                                                    })
                                                }

                                            </Grid>
                                        </Grid>
                                    </ListItem>
                                )

                            })
                        }

                    </List>

                </Grid>
            </>
        )
    }

    const RenderCredentialInfo = () => {
        return (
            <>
                <Grid item xs={12} sm={12} md={12} textAlign={"left"}>
                    <CommonSectionHeader
                        title={t('credential')}
                        subTitle={t('accessAuthentication')}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={12}>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                disabled
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"username"}
                                label={t('username')}
                                type="text"
                                variant="outlined"
                                value={values.username}
                                onChange={handleChange}
                            // error={touched.username && Boolean(errors.username)}
                            // helperText={touched.username && errors.username}
                            />
                        </Grid>

                        <Grid item xs={12} md={6} textAlign="left">
                            <FormControlLabel
                                control={
                                    <Switch
                                        sx={{
                                            ml: 2,
                                            my: 1
                                        }}
                                        name="status"
                                        checked={Boolean(values.status)}
                                        onChange={(event, checked) => {
                                            setFieldValue("status", Boolean(checked));
                                        }}
                                    />
                                }
                                label={
                                    Boolean(values.status) ? t('enabled') : t('disabled')
                                }
                            />
                        </Grid>

                        <Grid item xs={12} sm={12} md={12} textAlign="left">

                            <Divider>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            onChange={handleAllowChangePassword}
                                        />
                                    }
                                    label={t('updatePassword')}
                                />
                            </Divider>

                        </Grid>

                        {
                            RenderChangePassword()
                        }
                    </Grid>
                </Grid>
            </>
        )
    }

    const RenderProfileInfo = () => {
        return (
            <>
                <Grid item xs={12} sm={12} md={12} textAlign={"left"}>
                    <CommonSectionHeader
                        title={t('customerProfile')}
                        subTitle={t('fullDetailsOfCustomerInformation')}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={12}>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"firstname"}
                                label={t('firstName')}
                                type="text"
                                variant="outlined"
                                value={values.firstname}
                                onChange={handleChange}
                                error={touched.firstname && Boolean(errors.firstname)}
                                helperText={touched.firstname && errors.firstname}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"lastname"}
                                label={t('lastName')}
                                type="text"
                                variant="outlined"
                                value={values.lastname}
                                onChange={handleChange}
                                error={touched.lastname && Boolean(errors.lastname)}
                                helperText={touched.lastname && errors.lastname}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"contact"}
                                label={t('contact')}
                                type="text"
                                variant="outlined"
                                value={values.contact}
                                onChange={handleChange}
                                error={touched.contact && Boolean(errors.contact)}
                                helperText={touched.contact && errors.contact}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"email"}
                                label={t('email')}
                                type="text"
                                variant="outlined"
                                value={values.email}
                                onChange={handleChange}
                                error={touched.email && Boolean(errors.email)}
                                helperText={touched.email && errors.email}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </>
        )
    }

    const RenderAccessScopeInfo = () => {
        return (
            <>
                <Grid item xs={12} sm={12} md={12} textAlign={"left"}>
                    <CommonSectionHeader
                        title={t('accessScopes')}
                        subTitle={t('accessPermissionOfScopes')}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={12}>
                    <FormControl
                        margin="dense"
                        fullWidth
                        size="small"
                        error={touched.branchAccessId && Boolean(errors.branchAccessId)}
                    >
                        <InputLabel id="branch">{t('branch')}</InputLabel>
                        <Select
                            fullWidth
                            labelId="branchAccessId"
                            label={t('branch')}
                            name={"branchAccessId"}
                            onChange={handleChange}
                            value={values.branchAccessId}
                            renderValue={(selected) => {

                                return (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {
                                            selected.map((value, index2) => {

                                                // get back the branch name based on the id
                                                const branch = dataBranches.filter(i => i.id === value)

                                                const branchName = branch.length ? branch[0].branch_name : '-'

                                                return (
                                                    <Chip key={index2} label={branchName} />
                                                )
                                            })
                                        }
                                    </Box>
                                )

                            }}
                            multiple
                            input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                        >
                            {
                                dataBranches.map((i, index) => {
                                    return (
                                        <MenuItem
                                            key={index}
                                            value={i.id}
                                        >
                                            <Checkbox checked={values.branchAccessId.indexOf(i.id) > -1} />
                                            <ListItemText primary={`${i.branch_name} (${i.branch_code})`} />
                                        </MenuItem>
                                    )
                                })
                            }
                        </Select>
                        <FormHelperText>
                            {touched.branchAccessId && errors.branchAccessId}
                        </FormHelperText>
                    </FormControl>
                </Grid>
            </>
        )
    }

    const RenderForm = () => {

        if (!values) return <></>

        return (

            <form onSubmit={handleSubmit}>
                <StyledCard>
                    <Grid container spacing={1}>

                        {
                            RenderCredentialInfo()
                        }

                        <Grid item xs={12}>
                            <Divider sx={{ mt: 1, mb: 1 }} />
                        </Grid>

                        {
                            RenderProfileInfo()
                        }

                        <Grid item xs={12}>
                            <Divider sx={{ mt: 1, mb: 1 }} />
                        </Grid>

                        {
                            RenderAccessScopeInfo()
                        }

                        {
                            RenderCustomFieldSelection()
                        }

                    </Grid>
                </StyledCard>

                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <LoadingButton
                            type="submit"
                            // fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            loading={isLoading}
                        >
                            {t('saveChanges')}
                        </LoadingButton>
                    </Grid>
                </Grid>
            </form>

        )
    }

    return (
        <Box>

            {
                isPermissionAllow({ functionType: functionTypes.pageUpdate.id }) ?
                    RenderForm()
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}